/**
 * @file: useReportViewSummary.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 */

import { ReportShareViewSummary } from '@euler/model/ReportShareViewSummary';
import { useLocalObservable } from '@euler/utils/hooks';
import { of } from 'rxjs';
import { useReportShareViewSummary } from '../hooks';
import { useReportViewSummaryStore } from './ReportViewSummaryContext';
import { ReportViewSummaryEntry } from './ReportViewSummaryStore';

/**
 * The report read/view status of one order. Inside a list host that provides
 * a `ReportViewSummaryStore` the value comes from the store (fetched once,
 * only for visible cards); a standalone card without a store keeps fetching
 * on its own.
 */
export const useReportViewSummary = (
  orderNo: string | undefined,
): { summary: ReportShareViewSummary | null; loading: boolean } => {
  const store = useReportViewSummaryStore();
  const standalone = useReportShareViewSummary(store ? undefined : orderNo);

  const entry = useLocalObservable<ReportViewSummaryEntry | undefined>(
    () => (store && orderNo ? store.entry$(orderNo) : of(undefined)),
    () => (store && orderNo ? store.peek(orderNo) : undefined),
    [store, orderNo],
  );

  if (!store) return standalone;
  if (!orderNo) return { summary: null, loading: false };

  // `entry` lags by one render right after `orderNo` changes (the stream is
  // subscribed in an effect) — read the store directly in that window.
  const current = entry?.orderNo === orderNo ? entry : store.peek(orderNo);
  // not tracked yet (the card isn't visible / viewability hasn't fired):
  // keep the skeleton rather than rendering a status we haven't loaded.
  return {
    summary: current?.summary ?? null,
    loading: current?.loading ?? true,
  };
};
